import { useMemo } from 'react';

import { PairedTTestResultsDataV2, PairedTTestResultsV2 } from 'api/data/analyses';
import { Table } from 'components/UI/Table';
import { useTranslation } from 'hooks/store';
import { parseAnalysisNumber } from 'store/data/analyses/parsers';
import { AnalysisStatistic } from 'components/Analysis/Analyses';
import { useAnalysisErrorMessage } from 'hooks/store/data/analysis/useAnalysisErrorMessage';

interface Props {
	results: PairedTTestResultsV2;
}

export function ComparePairedTTestV2({ results }: Props) {
	const { translate } = useTranslation();

	const { data, error } = results;

	const translateMessage = useAnalysisErrorMessage(error);

	return (
		<AnalysisStatistic>
			<AnalysisStatistic.Title>
				{translate(({ analysis }) => analysis.statistics.pairedTTest.name)}
			</AnalysisStatistic.Title>

			{error && (
				<AnalysisStatistic.Error>
					{translateMessage ??
						translate(({ errors }) => errors.api.analyses.statistics.pairedTTest)}
				</AnalysisStatistic.Error>
			)}

			{data && <Result data={data} />}
		</AnalysisStatistic>
	);
}

function Result({ data }: { data: PairedTTestResultsDataV2 }) {
	const { tStatistic, pStatistic } = useMemo(() => {
		const tStatistic = getTStatistic({
			df: data.df,
			statistic: data.statistic
		});

		const pStatistic = getPStatistic({
			pValue: data.pValue
		});

		return { tStatistic, pStatistic };
	}, [data]);

	return (
		<Table.Responsive horizontalScroll fullWidth={false}>
			<Table>
				<Table.Body>
					<Table.Row>
						<Table.Cell title={tStatistic.text} width={14} noWrap>
							{tStatistic.JSX}
						</Table.Cell>
						<Table.Cell title={pStatistic.text} width={14} noWrap>
							{pStatistic.JSX}
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Table.Responsive>
	);
}

function getTStatistic(input: Omit<PairedTTestResultsDataV2, 'pValue'>) {
	const { df, statistic } = input;

	const { value: parsedTStatValue } = parseAnalysisNumber(statistic);

	const output = {
		JSX: (
			<>
				<i>{`t`}</i>
				{`(${df}) = ${parsedTStatValue}`}
			</>
		),
		text: `t(${df}) = ${parsedTStatValue}`
	};

	return output;
}

function getPStatistic(input: Omit<PairedTTestResultsDataV2, 'df' | 'statistic'>) {
	const { pValue } = input;

	const { operator, value: parsedPValue } = parseAnalysisNumber(pValue, {
		decimals: 3,
		formatAsPValue: true
	});

	const output = {
		JSX: (
			<>
				<i>{`p`}</i>
				{` ${operator} ${parsedPValue}`}
			</>
		),
		text: `p ${operator} ${parsedPValue}`
	};

	return output;
}
